import { toast } from "react-toastify";

// Centralized file type configurations
export const fileTypes = {
  document: {
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/vnd.ms-powerpoint': '.ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
    'application/pdf': '.pdf',
    'text/plain': '.txt',
    'application/zip': '.zip',
    'application/x-zip-compressed': '.zip'
  },
  image: {
    'image/jpeg': '.jpg, .jpeg',
    'image/png': '.png'
  },
  video: {
    'video/mp4': '.mp4',
    'video/x-msvideo': '.avi',
    'video/quicktime': '.mov'
  }
};

// Get MIME types from fileTypes configuration
export const mimeTypes = Object.entries(fileTypes).reduce((acc, [category, types]) => {
  acc[category] = Object.keys(types);
  return acc;
}, {});

// Centralized validation configurations
export const validationConfigs = {
  policyTemplate: {
    allowedTypes: ['document', 'image', 'video'],
    maxSizeInMB: 50,
    fileNameMaxLength: 100,
    maxFiles: 1
  },
  frameworkOrganization: {
    allowedTypes: ['image'],
    maxSizeInMB: 2,
    fileNameMaxLength: 100,
    maxFiles: 1
  },
  control: {
    allowedTypes: ['document', 'image', 'video'],
    maxSizeInMB: 10,
    fileNameMaxLength: 100,
    maxFiles: 10
  },
  default: {
    allowedTypes: ['document'],
    maxSizeInMB: 5,
    fileNameMaxLength: 100,
    maxFiles: 1
  },
  // Governance Questionnaire "Extract" upload — the client's question dump (paste, PDF, docx,
  // xlsx, or a screenshot). Legacy .xls / .doc are rejected with a "save as .xlsx / .docx"
  // message (rejectLegacyOffice) because the extractor cannot parse them.
  questionnaire: {
    allowedTypes: ['document', 'image'],
    rejectLegacyOffice: true,
    maxSizeInMB: 20,
    fileNameMaxLength: 150,
    maxFiles: 1
  }
};

export const getFormattedFileTypes = (types) => {
  return types.reduce((acc, type) => {
    if (fileTypes[type]) {
      acc[type] = Object.values(fileTypes[type])
        .flat()
        .map(ext => ext.split(', '))
        .flat();
    }
    return acc;
  }, {});
};

/**
 * validateFile function to validate uploaded files.
 * 
 * @param {Object} file - The uploaded file object (contains size, type, etc.)
 * @param {Object} config - Configuration object defining allowed types and max size.
 * @param {Array} config.allowedTypes - Array of allowed types (e.g., ['image', 'document', 'video']).
 * @param {Number} [config.maxSizeInMB=5] - Maximum file size in MB. Defaults to 5 MB.
 * 
 * @returns {Boolean} - Returns true if validation passes, false otherwise.
 */
export const validateFile = (file, config) => {
  const validTypes = config.allowedTypes.reduce((acc, type) => {
    return acc.concat(mimeTypes[type] || []);
  }, []);

  const maxSizeInMB = config.maxSizeInMB || 5;
  const fileNameMaxLength = config.fileNameMaxLength || 100;

  if (file.name.length > fileNameMaxLength) {
    toast.error("Uploaded file name length too long");
    toast.info("Please shorten your file name and try again");
    return false;
  }

  if (config.rejectLegacyOffice) {
    const lower = (file.name || '').toLowerCase();
    const legacyMime = ['application/vnd.ms-excel', 'application/msword'].includes(file.type);
    if (legacyMime || /\.(xls|doc)$/.test(lower)) {
      toast.error('Legacy .xls / .doc files are not supported. Open the file and Save As .xlsx or .docx (or export a PDF), then upload it again.');
      return false;
    }
  }

  if (!validTypes.includes(file.type)) {
    toast.error("Invalid file type. Only "+config.allowedTypes+" file type allowed");
    toast.info("Please upload a valid file type and try again");
    return false;
  }

  if (file.size > maxSizeInMB * 1024 * 1024) {
    toast.error(`File size exceeds the maximum allowed size of ${maxSizeInMB}MB`);
    toast.info(`Please upload a file size less than ${maxSizeInMB}MB and try again`);
    return false;
  }

  return true;
};

export const handleFileUploadChange = async (event, options) => {
  const {
    setState,
    handleFileUpload,
    validateOptions = { 
      allowedTypes: ['document'], 
      maxSizeInMB: 5,
      fileNameMaxLength: 100 
    }
  } = options;

  const file = event.target.files[0];
  if (!file) return;

  const isValidFile = validateFile(file, validateOptions);
  if (!isValidFile) {
    setState((prevState) => ({
      ...prevState,
      error: " ",
    }));
    return;
  }

  const fileId = await handleFileUpload(file);
  if (fileId) {
    setState((prevState) => ({ 
      ...prevState, 
      uploadedFileId: fileId 
    }));
    toast.success("File uploaded successfully");
  } else {
    setState((prevState) => ({
      ...prevState,
      error: "",
      uploadedFile: null,
      companyLogo: null,
      imageName: "",
      imageSize: "",
      uploadedFileId: null,
      logo1Store: null,
      logo1Name: "",
      logo1Size: "",
    }));
    toast.error("File upload failed. Please try again.");
  }
};

/**
 * Checks if a file is of type docx or doc
 * 
 * @param {String} mimeType - The MIME type of the file
 * @returns {Boolean} - Returns true if the file is a docx or doc, false otherwise
 */
export const isDocxOrDocFile = (mimeType) => {
  const docMimeTypes = [
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // .docx
  ];
  
  return docMimeTypes.includes(mimeType);
};

/**
 * Checks if a URL's file extension is docx or doc
 * 
 * @param {String} url - The URL or filename to check
 * @returns {Boolean} - Returns true if the file appears to be docx or doc based on extension, false otherwise
 */
export const hasDocxOrDocExtension = (url) => {
  if (!url) return false;
  
  const lowercasedUrl = url.toLowerCase();
  return lowercasedUrl.endsWith('.docx') || lowercasedUrl.endsWith('.doc');
};
  